import { useState } from 'react';
import { Button, FormField } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
import { FieldError, FormFooter, type FormProps, SourceFields, useQuestionSource } from './shared';

const MAX_LEFT = 10;
const MAX_RIGHT = 15;
interface Row { left: string; right: string }

function fromQuestion(q: FormProps['initial']): { rows: Row[]; extras: string[] } {
  if (!q || !q.left || !q.right || !q.pairs) return { rows: [{ left: '', right: '' }, { left: '', right: '' }], extras: [] };
  const used = new Set<number>();
  const rows = q.left.map((l, i) => {
    const pair = q.pairs!.find((p) => p[0] === i);
    if (pair) used.add(pair[1]);
    return { left: l, right: pair ? q.right![pair[1]] ?? '' : '' };
  });
  return { rows, extras: q.right.filter((_, j) => !used.has(j)) };
}

export default function MatchingForm({ initial, saving, errors, onSave, onCancel, chapters }: FormProps) {
  const init = fromQuestion(initial);
  const [text, setText] = useState(initial?.text ?? '');
  const [rows, setRows] = useState<Row[]>(init.rows);
  const [extras, setExtras] = useState<string[]>(init.extras);
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');
  const source = useQuestionSource(initial);
  const [local, setLocal] = useState<Record<string, string>>({});

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const err: Record<string, string> = {};
    if (!text.trim()) err.text = 'Enter the question.';
    if (rows.length < 2) err.left = 'Add at least two pairs.';
    else if (rows.some((r) => !r.left.trim() || !r.right.trim())) err.left = 'Fill in both sides of every pair.';
    setLocal(err);
    if (Object.keys(err).length) return;
    // Pairs are implicit by row: row i's right text is right index i. Extra
    // choices follow the paired rows, so they are in `right` only. The student
    // app shuffles the right side, so this order never leaks the answer.
    onSave({
      type: 'matching', text: text.trim(),
      left: rows.map((r) => r.left.trim()),
      right: [...rows.map((r) => r.right.trim()), ...extras.map((x) => x.trim()).filter(Boolean)],
      pairs: rows.map((_, i) => [i, i] as [number, number]),
      ...(explanation.trim() ? { explanation: explanation.trim() } : {}), ...source.payload,
    });
  };
  const e = { ...local, ...errors };
  const setRow = (i: number, patch: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <FormField label="Question text" htmlFor="mt-text" required error={e.text}>
        <textarea id="mt-text" rows={2} className={fieldClass} value={text} onChange={(ev) => setText(ev.target.value)} />
      </FormField>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-muted">
          <span>Left side</span><span>Matching right side</span><span className="w-9" />
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
            <input aria-label={`Left item ${i + 1}`} className={fieldClass} value={r.left} onChange={(ev) => setRow(i, { left: ev.target.value })} />
            <input aria-label={`Right item ${i + 1}`} className={fieldClass} value={r.right} onChange={(ev) => setRow(i, { right: ev.target.value })} />
            <button type="button" aria-label={`Remove pair ${i + 1}`} disabled={rows.length <= 2}
              className="h-9 w-9 rounded-lg text-muted-2 hover:bg-danger-bg hover:text-danger-ink disabled:opacity-40"
              onClick={() => setRows(rows.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <FieldError error={e.left} />
        <FieldError error={e.pairs} />
        <Button type="button" variant="ghost" size="sm" disabled={rows.length >= MAX_LEFT || rows.length + extras.length >= MAX_RIGHT}
          onClick={() => setRows([...rows, { left: '', right: '' }])}>Add pair</Button>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted">Extra choices that match nothing (optional)</p>
        {extras.map((x, i) => (
          <div key={i} className="flex items-center gap-2">
            <input aria-label={`Extra choice ${i + 1}`} className={fieldClass} value={x}
              onChange={(ev) => setExtras(extras.map((v, j) => (j === i ? ev.target.value : v)))} />
            <button type="button" aria-label={`Remove extra choice ${i + 1}`}
              className="h-9 w-9 rounded-lg text-muted-2 hover:bg-danger-bg hover:text-danger-ink"
              onClick={() => setExtras(extras.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <FieldError error={e.right} />
        <Button type="button" variant="ghost" size="sm" disabled={rows.length + extras.length >= MAX_RIGHT}
          onClick={() => setExtras([...extras, ''])}>Add extra choice</Button>
      </div>
      <FormField label="Explanation (optional)" htmlFor="mt-expl" error={e.explanation}>
        <textarea id="mt-expl" rows={2} className={fieldClass} value={explanation} onChange={(ev) => setExplanation(ev.target.value)} />
      </FormField>
      <SourceFields source={source} chapters={chapters} errors={e} idPrefix="mt" />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}
